import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminGuests = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialog, setDialog] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });
  const navigate = useNavigate();

  const showAlert = (title, message, onConfirm = null) => {
    setDialog({ isOpen: true, type: 'alert', title, message, onConfirm });
  };

  const showConfirm = (title, message, onConfirm) => {
    setDialog({ isOpen: true, type: 'confirm', title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    fetchGuests();
  }, [currentPage, searchTerm]);

  const fetchGuests = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: '10'
      });

      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/guests/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch guests');

      const data = await response.json();
      setGuests(data.items);
      setTotalPages(data.pages);
    } catch (error) {
      console.error('Error fetching guests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (guest) => {
    navigate(`/admin/guests/edit/${guest.id}`);
  };

  const executeDelete = async (guestId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/guests/${guestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to delete guest');

      fetchGuests();
      showAlert('Deleted', 'Guest has been successfully deleted.');
    } catch (error) {
      console.error('Error deleting guest:', error);
      showAlert('Error', 'Failed to delete guest');
    }
  };

  const handleDelete = (guestId) => {
    showConfirm(
      'Delete Guest',
      'Are you sure you want to completely delete this guest? This action cannot be undone.',
      () => executeDelete(guestId)
    );
  };

  const GuestCard = ({ guest }) => (
    <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-4 md:p-6 hover:scale-[1.02] transition-transform duration-300">
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
        <img
          src={guest.image_url || guest.imageUrl}
          alt={guest.name}
          className="w-24 h-24 sm:w-20 sm:h-20 object-cover rounded-full"
        />
        <div className="flex-1 w-full">
          <h3 className="font-bold text-warm-white text-xl mb-1">{guest.name}</h3>
          <p className="text-electric-yellow text-sm mb-2">{guest.title_role || guest.titleRole}</p>
          <p className="text-soft-gray text-sm mb-3">{guest.bio.substring(0, 100)}...</p>
          <div className="flex items-center gap-4 text-sm text-soft-gray mb-4">
            <span>🎙️ {guest.episode_count} episodes</span>
          </div>
          <p className="text-electric-yellow text-sm italic">"{guest.quote}"</p>
        </div>
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-2 pt-4 border-t border-slate-dark/30 mt-4 w-full sm:w-auto">
        <button
          onClick={() => handleEdit(guest)}
          className="flex-1 sm:flex-none bg-electric-yellow hover:bg-electric-yellow/90 text-midnight px-4 py-2 rounded-lg transition-colors duration-300 text-center font-bold"
        >
          Edit
        </button>
        <button
          onClick={() => handleDelete(guest.id)}
          className="flex-1 sm:flex-none bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-center"
        >
          Delete
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-electric-yellow text-lg">Loading guests...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="font-clash text-2xl md:text-3xl font-bold text-electric-yellow">Guests</h2>
        <button
          onClick={() => navigate('/admin/guests/create')}
          className="w-full sm:w-auto bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-colors duration-300 text-center"
        >
          + Add Guest
        </button>
      </div>



      {/* Search */}
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
        <input
          type="text"
          placeholder="Search guests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
        />
      </div>

      {/* Guests List */}
      <div className="space-y-4">
        {guests.length === 0 ? (
          <div className="text-center py-12 text-soft-gray">
            No guests found. Create your first guest!
          </div>
        ) : (
          guests.map((guest) => (
            <GuestCard key={guest.id} guest={guest} />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="bg-charcoal/40 text-warm-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-electric-yellow/10 transition-colors duration-300"
          >
            Previous
          </button>
          <span className="text-soft-gray">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="bg-charcoal/40 text-warm-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-electric-yellow/10 transition-colors duration-300"
          >
            Next
          </button>
        </div>
      )}

      {/* Custom Dialog Modal */}
      {dialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-midnight/80 backdrop-blur-sm">
          <div className="bg-slate-dark border border-electric-yellow/30 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-electric-yellow mb-2">{dialog.title}</h3>
            <p className="text-soft-gray mb-6">{dialog.message}</p>
            <div className="flex justify-center gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg font-medium text-soft-gray hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                  closeDialog();
                }}
                className={`px-4 py-2 rounded-lg font-bold transition-transform hover:scale-105 ${
                  dialog.type === 'confirm' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' 
                    : 'bg-electric-yellow text-midnight shadow-lg shadow-electric-yellow/20'
                }`}
              >
                {dialog.type === 'confirm' ? 'Delete' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGuests;